import Link from 'next/link';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { DesktopLogo } from 'components/AppBar/DesktopLogo';
import { FeaturedTestimonial } from 'components/FeaturedTestimonial';
import { TwitterSignIn } from 'features/SignIn/TwitterSignIn';
import { useRouter } from 'next/router';
import { GoogleSignInWrapper } from 'features/SignIn/GoogleSignIn';
import { logFirebaseDebugInfo } from 'lib/utils/firebaseAuthUtils';
import { useCurrentUser } from 'lib/contexts/ApplicationState';
import { useFirebaseAuth } from 'lib/contexts/firebaseAuthContext';
import { EmailSignUp } from 'features/SignIn/EmailAuth/EmailAuth';
import { AppleSignIn } from 'features/SignIn/AppleSignIn';
import { InstagramCreatorSignin } from 'features/SignIn/InstagramCreatorSignin';
import { ReferralSpeech } from './ReferralSpeech';
import { Welcome } from './Welcome';

interface Props {
  referredBy: string;
  verifiedEmail?: string;
}

export const SignUp = ({ referredBy, verifiedEmail }: Props) => {
  const router = useRouter();
  const currentUser = useCurrentUser();
  const { firebaseUser } = useFirebaseAuth();
  const returnTo = referredBy ? `/signup?referredBy=${referredBy}` : (router.query?.returnTo || '/');
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    if (router.query.networkErrCode && router.query.networkErrMessage) {
      enqueueSnackbar(router.query.networkErrMessage as string, {
        variant: 'error',
      });
      router.push({
        pathname: '/signup',
        query: {
          ...router.query,
          networkErrCode: [],
          networkErrMessage: [],
          networkName: [],
        },
      });
    }
  }, [router.query, router, enqueueSnackbar]);

  const [showEmail, setShowEmail] = useState(!!verifiedEmail);
  logFirebaseDebugInfo(currentUser, firebaseUser);

  // Redirect to appropriate step if user is authenticated
  useEffect(() => {
    if (currentUser) {
      if (!currentUser.username) {
        router.push({
          pathname: '/signup/username',
          query: {
            ...router.query,
          },
        });
      } else if (!currentUser.termsAcknowledged) {
        router.push({
          pathname: '/signup/finish',
          query: {
            ...router.query,
          },
        });
      } else {
        router.push('/');
      }
    }
  }, [currentUser, router]);

  return (
    <div className="flex flex-wrap-reverse items-center md:h-full">
      <div className="h-full bg-teal md:w-1/2 flex items-center justify-center p-6">
        <div className="md:p-6">
          {referredBy ? <ReferralSpeech referredBy={referredBy} /> : <FeaturedTestimonial />}
        </div>
      </div>
      <div className="w-full md:w-1/2 p-6 text-center flex items-center">
        <div className="w-full">
          <Welcome />
          <div className="max-w-[180px] m-auto mb-4">
            <DesktopLogo />
          </div>
          <div className="mb-8">
            {!showEmail && (
              <>
                <h2 className="text-xl mb-4">Choose your sign up method</h2>
                <div className="mb-4">
                  <GoogleSignInWrapper returnTo={returnTo as string} />
                </div>
                <div className="mb-4">
                  <AppleSignIn returnTo={returnTo as string} />
                </div>
                <div className="mb-4">
                  <TwitterSignIn returnTo={returnTo as string} />
                </div>
                {router.query.instagramTest && (
                  <div className="mb-4">
                    <InstagramCreatorSignin returnTo={returnTo as string} />
                  </div>
                )}
              </>
            )}
            <div className="mb-4">
              <EmailSignUp
                onShow={setShowEmail}
                verifiedEmail={verifiedEmail}
                returnTo={returnTo as string}
              />
            </div>
          </div>
          {!showEmail && (
            <>
              <div className="text-lg mb-4">
                <span className="font-semibold">Already have a Gondola account?</span>
                {' '}
                <Link
                  href={{ pathname: '/signin', query: router.query }}
                  className="underline whitespace-nowrap"
                >

                  Sign In

                </Link>
              </div>
              <div className="flex flex-col justify-end">
                <span>
                  By continuing I agree to the
                  {' '}
                  <Link href="/privacy" className="underline">

                    Privacy Policy

                  </Link>
                  {' '}
                  and
                  {' '}
                  <Link href="/terms" className="underline">

                    Terms of Service

                  </Link>
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
